// Time left on a running free trial: a banner across the app, and a small
// timer in practice and exams. When a trial ends, access is refreshed and the
// banner offers the product instead (for a day, or until dismissed).
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Alert, Button, Chip, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { countdownText, type MyTrial, trialKeys, useMyTrials } from './trialService';

const DAY = 86_400_000;
const DISMISSED_KEY = 'tradeprep.endedTrialsDismissed';

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/** The current time, ticking every second while `active`. */
function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return now;
}

const running = (t: MyTrial, now: number) => t.active && !!t.expires_at && Date.parse(t.expires_at) > now;

/** When a running trial runs out, refresh everything that shows access. */
function useRefreshOnEnd(trials: MyTrial[], now: number) {
  const qc = useQueryClient();
  const seen = useRef(new Set<string>());
  useEffect(() => {
    for (const t of trials) {
      if (t.active && t.expires_at && Date.parse(t.expires_at) <= now && !seen.current.has(t.product_id)) {
        seen.current.add(t.product_id);
        void qc.invalidateQueries({ queryKey: trialKeys.mine });
        void qc.invalidateQueries({ queryKey: ['library'] });
        void qc.invalidateQueries({ queryKey: ['store-product', t.product_slug] });
      }
    }
  }, [trials, now, qc]);
}

export function TrialBanner() {
  const trials = useMyTrials().data ?? [];
  const now = useNow(trials.some((t) => t.active));
  useRefreshOnEnd(trials, now);
  const [dismissed, setDismissed] = useState(readDismissed);
  const dismiss = (id: string) => {
    const next = [...dismissed, id].slice(-50);
    setDismissed(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private mode: it just shows again next visit.
    }
  };

  const live = trials.filter((t) => running(t, now));
  const ended = trials.filter(
    (t) => !running(t, now) && t.expires_at && now - Date.parse(t.expires_at) < DAY && !dismissed.includes(`${t.product_id}:${t.claimed_at}`),
  );
  if (!live.length && !ended.length) return null;
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {live.map((t) => (
        <Alert
          key={t.product_id}
          severity="info"
          icon={<TimerOutlinedIcon />}
          action={
            <Button color="inherit" component={RouterLink} to={`/store/${t.product_slug}`}>
              Buy now
            </Button>
          }
        >
          Free trial of <strong>{t.product_title}</strong>: <strong>{countdownText(Date.parse(t.expires_at!) - now)}</strong> left
        </Alert>
      ))}
      {ended.map((t) => (
        <Alert
          key={t.product_id}
          severity="warning"
          onClose={() => dismiss(`${t.product_id}:${t.claimed_at}`)}
          action={
            <Button color="inherit" component={RouterLink} to={`/store/${t.product_slug}`}>
              See options
            </Button>
          }
        >
          Your free trial of <strong>{t.product_title}</strong> has ended. Buy it to keep studying.
        </Alert>
      ))}
    </Stack>
  );
}

/** Compact time left for a trial that opens this question bank (practice and exam headers). */
export function TrialTimer({ bankId }: { bankId: string | null | undefined }) {
  const trials = useMyTrials().data ?? [];
  const t = bankId ? trials.find((x) => x.active && x.question_bank_ids.includes(bankId)) : undefined;
  const now = useNow(!!t);
  useRefreshOnEnd(trials, now);
  if (!t?.expires_at) return null;
  const left = Date.parse(t.expires_at) - now;
  return (
    <Chip
      size="small"
      icon={<TimerOutlinedIcon />}
      color={left <= 0 ? 'default' : left < 5 * 60_000 ? 'warning' : 'info'}
      label={left <= 0 ? 'Trial ended' : `Trial ${countdownText(left)}`}
      aria-label={left <= 0 ? 'Your free trial has ended' : `Free trial: ${countdownText(left)} left`}
    />
  );
}
